import React, { useContext, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Dialog from 'src/components/common/Dialog.redesign';
import ProjectStaff from 'src/models/ProjectStaff';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import BillingAndPaymentCycle from 'src/models/BillingAndPaymentCycle';
import useFetch from 'use-http';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import ProjectAdministratorStaff from 'src/models/ProjectAdministratorStaff';
import User from 'src/models/User';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import ProjectWorkClassification from 'src/models/ProjectWorkClassification';

interface IStaffingLandmanDialog {
    landmanInfo: ProjectStaff;
    onCloseDialog?: () => void;
    billingCycle: BillingAndPaymentCycle[];
    pcapmList: User[];
    projectAdminStaff: ProjectAdministratorStaff[];
    workClassifications: ProjectWorkClassification[];
    updateCardListener: (data: boolean) => void;
}

const StaffingLandmanDialog = (props: IStaffingLandmanDialog) => {
    const { register, handleSubmit, errors, reset } = useForm();
    const { put, response } = useFetch();
    const appContext = useContext<IAppContextState>(AppContext);

    const [formData, setFormData] = useState(undefined);

    useEffect(() => {
        prepFormData();
    }, []);

    useEffect(() => {
        reset(formData);
    }, [formData]);

    const prepFormData = () => {
        let data: ProjectStaff = {
            overrideBillRate: props?.landmanInfo ? props?.landmanInfo?.overrideBillRate : null,
            overridePayRate: props?.landmanInfo ? props?.landmanInfo?.overridePayRate : null,
            pcapmId: props?.landmanInfo ? props?.landmanInfo?.pcapmId : null,
            projectManagerId: props?.landmanInfo ? props?.landmanInfo?.projectManagerId : null,
            startBillingCycleId: props?.landmanInfo ? props?.landmanInfo?.startBillingCycle?.id : 0,
            endBillingCycleId: props?.landmanInfo ? props?.landmanInfo?.endBillingCycle?.id : 0,
            workClassificationId: props?.landmanInfo ? props?.landmanInfo?.workClassificationId : null,
            ownershipAccess: props?.landmanInfo ? props?.landmanInfo?.ownershipAccess : null,
            reportAccess: props?.landmanInfo ? props?.landmanInfo?.reportAccess : null,
            contractAccess: props?.landmanInfo ? props?.landmanInfo?.contractAccess : null,
        };

        setFormData(data);
    };

    const payPeriods = () => {
        return props?.billingCycle
            ? props?.billingCycle?.map((c: BillingAndPaymentCycle) => {
                  let year = new Date(c?.endDate).getFullYear();
                  if (c.cycleNumber <= 9) {
                      return {
                          value: c.id,
                          label: `${year} - 0${c.cycleNumber}`,
                      };
                  }

                  return {
                      value: c.id,
                      label: `${year} - ${c.cycleNumber}`,
                  };
              })
            : [];
    };

    const formatData = (values: ProjectStaff) => {
        let data: ProjectStaff = {
            id: 0,
            userId: null,
            startDate: null,
            endDate: null,
            projectAdministratorId: null,
            pcapmId: null,
            workClassificationId: null,
            isOverride: null,
            startBillingCycleId: null,
            startBillingCycle: null,
            endBillingCycleId: null,
            overrideBillRate: null,
            overridePayRate: null,
            ownershipAccess: true,
            contractAccess: false,
            reportAccess: false,
        };

        data.id = props?.landmanInfo ? props?.landmanInfo?.id : null;
        data.userId = props?.landmanInfo ? props?.landmanInfo?.userId : null;
        data.projectAdministratorId = props?.landmanInfo ? props?.landmanInfo?.projectAdministratorId : null;
        data.startBillingCycleId = values.startBillingCycleId ? parseInt(values.startBillingCycleId + '', 10) : null;
        data.startBillingCycle = props?.billingCycle
            ? props?.billingCycle?.find((bc) => parseInt(data.startBillingCycleId + '', 10) === bc.id)
            : null;
        data.endBillingCycleId = values.endBillingCycleId ? parseInt(values.endBillingCycleId + '', 10) : null;
        data.endBillingCycle = props?.billingCycle ? props?.billingCycle?.find((bc) => parseInt(data.endBillingCycleId + '', 10) === bc.id) : null;
        data.overrideBillRate = values.overrideBillRate ? parseInt(values.overrideBillRate + '', 10) : null;
        data.overridePayRate = values.overridePayRate ? parseInt(values.overridePayRate + '', 10) : null;
        data.pcapmId = values.pcapmId ? parseInt(values.pcapmId + '', 10) : null;
        data.projectManagerId = values.projectManagerId ? parseInt(values.projectManagerId + '', 10) : null;
        data.workClassificationId = values.workClassificationId ? parseInt(values.workClassificationId + '', 10) : null;
        data.ownershipAccess = !!values.ownershipAccess ? values.ownershipAccess : false;
        data.reportAccess = !!values.reportAccess ? values.reportAccess : false;
        data.contractAccess = !!values.contractAccess ? values.contractAccess : false;

        if (data.endBillingCycleId === data.startBillingCycleId) {
            data.endBillingCycleId = parseInt(values.endBillingCycleId + '', 10) + 6;
            data.endBillingCycle = props?.billingCycle
                ? props?.billingCycle?.find((bc) => parseInt(data.endBillingCycleId + '', 10) === bc.id)
                : null;
        }

        return data;
    };

    const updateLandmanInfo = async (data: ProjectStaff) => {
        const result = await put(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/projectstaff/staffing`, data);

        if (response.ok) {
            console.log('Updated Landman info', result);
            props.updateCardListener(true);
        }
    };

    const onSubmit = (values: ProjectStaff) => {
        const formatedData = formatData(values);

        if (!!formatedData.id && formatedData.id > 0) {
            updateLandmanInfo(formatedData);
        }

        props.onCloseDialog();
    };

    return (
        <Dialog title="Landmen Details">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div style={{ marginTop: '2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem', marginLeft: '5rem' }}>
                        <RHFInputBox name="overrideBillRate" label="New Bill Rate" type="text" register={register} errors={errors} />

                        <RHFInputBox name="overridePayRate" label="New Pay Rate" type="number" register={register} errors={errors} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3rem', marginLeft: '5rem' }}>
                        <RHFSelect
                            name="pcapmId"
                            label="PC/APM"
                            options={
                                props?.pcapmList
                                    ? props.pcapmList
                                          .filter((u) => u?.role?.id === 3)
                                          .map((u) => {
                                              return { value: u.id, label: `${u.firstName} ${u.lastName}` };
                                          })
                                    : []
                            }
                            register={register}
                            errors={errors}
                        />

                        <RHFSelect
                            name="projectManagerId"
                            label="Project Manager"
                            options={
                                props?.pcapmList
                                    ? props.pcapmList
                                          .filter((u) => u?.role?.id === 2)
                                          .map((u) => {
                                              return { value: u.id, label: `${u.firstName} ${u.lastName}` };
                                          })
                                    : []
                            }
                            register={register}
                            errors={errors}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3rem', marginLeft: '5rem' }}>
                        <RHFSelect label="Start Pay Period" name="startBillingCycleId" options={payPeriods()} register={register} errors={errors} />
                        <RHFSelect label="End Pay Period" name="endBillingCycleId" options={payPeriods()} register={register} errors={errors} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3rem', marginLeft: '5rem' }}>
                        <RHFSelect
                            name="workClassificationId"
                            label="Work Classification"
                            options={
                                props?.workClassifications
                                    ? props.workClassifications.map((wc) => {
                                          return { value: wc.id, label: `${wc.alias} - ${wc.name}` };
                                      })
                                    : []
                            }
                            register={register}
                            errors={errors}
                        />
                    </div>

                    <div style={{ textAlign: 'center' }}>
                        <RHFLegend title="Landman Permissions" />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3rem', marginLeft: '1rem' }}>
                        <RHFCheckbox
                            bodyStyle={{ display: 'flex', alignItems: 'center' }}
                            label="Ownership Access"
                            name="ownershipAccess"
                            register={register}
                            errors={errors}
                        />
                        <RHFCheckbox
                            bodyStyle={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
                            label="Contract Access"
                            name="contractAccess"
                            register={register}
                            errors={errors}
                        />
                        <RHFCheckbox
                            bodyStyle={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
                            label="Report Access"
                            name="reportAccess"
                            register={register}
                            errors={errors}
                        />
                    </div>
                    <Button text="Save" intent="primary" type="submit" />
                </div>
            </form>
        </Dialog>
    );
};

export default StaffingLandmanDialog;



// WEBPACK FOOTER //
// ./src/components/project/sections/StaffingLandmanDialog.redesign.tsx